import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Dropdown, Spinner } from 'react-bootstrap';
import { EllipsisV } from '@styled-icons/fa-solid';
import DataTable, { useDataTableHandle } from '../../DataTable';
import { getMasterDataLoadType, upsertMasterDataLoadType } from '../../apiCalls';
import LoadModal from './loadModal';
import ConfirmationModal from '../../ConfirmationModal';
import ToastNotification from '../../Toast/ToastNotification';
import { RefDataContext } from '../Maintenance';

const Load = () => {
  const [load, setLoad] = useState([]);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState({ type: 'success', message: '' });
  const [showConfirmDelete, setShowConfirmDelete] = useState(null);
  const [paginationLoaded, setPaginationLoaded] = useState(false);

  const dth = useDataTableHandle();
  const { registerAddHandler } = useContext(RefDataContext);

  const updateData = useCallback(async (setPaginationLength, pagination) => {
    try {
      setLoading(true);
      const pageNumber = pagination.pageNo + 1;
      const response = await getMasterDataLoadType(pageNumber, pagination.itemsPerPage, 0);
      const data = response?.data ?? (Array.isArray(response) ? response : []);
      const totalItems = response?.totalCount ?? response?.totalItems ?? data.length;
      setLoad(data);
      setPaginationLength(totalItems);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshLoad = useCallback(() => {
    if (!dth?.current?.pagination?.itemsPerPage) return;
    updateData(dth.current.setServerDataLength, dth.current.pagination);
  }, [updateData, dth]);

  useEffect(() => {
    registerAddHandler('load', () => setSelectedLoad({ id: -1 }));
  }, [registerAddHandler]);

  useEffect(() => {
    if (!paginationLoaded || !dth?.current?.pagination?.itemsPerPage) return;
    updateData(dth.current.setServerDataLength, dth.current.pagination);
  }, [paginationLoaded, updateData]);

  const handleDelete = async (row) => {
    try {
      await upsertMasterDataLoadType({
        id: row.id,
        loadType: row.loadType,
        PackageCode: row.PackageCode || '',
        CDSPackageCode: row.CDSPackageCode || '',
        softDelete: 1,
      });
      setShowConfirmDelete(null);
      setToastConfig({ type: 'success', message: 'Package type deleted successfully!' });
      setShowToast(true);
      refreshLoad();
    } catch (err) {
      setToastConfig({ type: 'error', message: err.message || 'Failed to delete package type' });
      setShowToast(true);
    }
  };

  return (
    <>
      <ToastNotification
        show={showToast}
        onClose={() => setShowToast(false)}
        type={toastConfig.type}
        message={toastConfig.message}
      />

      <LoadModal
        load={selectedLoad}
        onSave={(isNew) => {
          setToastConfig({
            type: 'success',
            message: isNew ? 'Package type added successfully!' : 'Package type updated successfully!',
          });
          setShowToast(true);
          setSelectedLoad(null);
          refreshLoad();
        }}
        onError={(error) => {
          setToastConfig({ type: 'error', message: error || 'Failed to save package type' });
          setShowToast(true);
        }}
        onCancel={() => setSelectedLoad(null)}
      />

      {loading && (
        <span className="d-flex align-items-center gap-2 mb-2">
          <Spinner animation="border" size="sm" /> Loading...
        </span>
      )}

      <DataTable
        idKey="id"
        handle={dth}
        data={load}
        className="table-striped"
        lazyLoading
        onUpdate={updateData}
        onPaginationLoaded={() => setPaginationLoaded(true)}
        isLoading={loading}
      >
        <DataTable.Headers
          style={{
            fontSize: '14.4px',
            backgroundColor: '#fff',
            paddingBottom: '10px',
            borderBottom: '1px solid #ddd',
            borderTop: '1px solid #ddd',
          }}
        />
        <DataTable.Pagination itemsPerPage={[10, 25, 50, 100]} className="w-100 d-flex justify-content-end" />
        <DataTable.Row style={{ fontSize: '15px' }} />
        <DataTable.Column colKey="loadType" header="Package Type" />
        <DataTable.Column colKey="PackageCode" header="Thyme-IT Package Code" />
        <DataTable.Column colKey="CDSPackageCode" header="Descartes Package Code" />
        <DataTable.Column
          colKey="actions"
          header=""
          hideFilter
          children={(item) => (
            <Dropdown className="w-100 h-100 d-grid center">
              <Dropdown.Toggle as="button" className="dot-menu-button">
                <EllipsisV width={'0.5rem'} />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item as="button" onClick={() => setSelectedLoad(item)}>
                  View/Edit
                </Dropdown.Item>
                <Dropdown.Item as="button" onClick={() => setShowConfirmDelete(item)}>
                  Remove
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}
        />
      </DataTable>

      <ConfirmationModal
        header="Confirm Deletion"
        show={!!showConfirmDelete}
        onHide={() => setShowConfirmDelete(null)}
        onConfirm={() => showConfirmDelete && handleDelete(showConfirmDelete)}
      >
        Are you sure you want to delete this record?
      </ConfirmationModal>
    </>
  );
};

export default Load;
